import React from 'react'
import { useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CBreadcrumb, CBreadcrumbItem } from '@coreui/react-pro'
import routePaths from '../../routes/route-paths'
import { useTrackerStrategyName } from '../../pages/portfolio-tracker/tracker-setup'

interface RouteMapping {
  path: string
  name: string
  hasDetails?: boolean
}

const AppBreadcrumb: React.FC = () => {
  const { pathname: currentLocation, search } = useLocation()
  const { t } = useTranslation()
  const trackerStrategyName = useTrackerStrategyName()

  // Map routes to display names
  const routeNames: RouteMapping[] = [
    { path: routePaths.Dashboard, name: t('common:navigation.dashboard') },
    { path: routePaths.Customers, name: t('common:navigation.customers') },
    { path: routePaths.Symbols, name: t('common:navigation.symbols') },
    { path: routePaths.Templates, name: t('common:navigation.templates') },
    { path: routePaths.Baskets, name: t('common:navigation.baskets') },
    { path: routePaths.Offers, name: t('common:navigation.offers') },
    { path: routePaths.MembershipPlatforms, name: t('common:navigation.memberships') },
    { path: routePaths.AddOns, name: t('common:navigation.addOns') },
    { path: routePaths.IncomingSignals, name: t('common:navigation.incomingSignals') },
    { path: routePaths.StrategyDataAlerts, name: t('common:navigation.strategyDataAlerts') },
    { path: routePaths.InfoAlerts, name: t('common:navigation.infoAlerts') },
    { path: routePaths.UnknownAlerts, name: t('common:navigation.unknownAlerts') },
    { path: routePaths.MarketCandleRuns, name: t('common:navigation.marketCandles') },
    { path: routePaths.PortfolioBuilder, name: t('common:navigation.portfolioBuilder') },
    { path: routePaths.PortfolioTracker, name: t('common:navigation.portfolioTracker') },
    { path: routePaths.Webhooks, name: t('common:navigation.webhooks') },
    { path: routePaths.StrategyRequests, name: t('common:navigation.strategyRequests'), hasDetails: false },
  ]

  // Handle dynamic routes like /customers/:id
  const getRouteName = (pathname: string): string | null => {
    // First check exact match
    const exactMatch = routeNames.find((route) => route.path === pathname)
    if (exactMatch) {
      return exactMatch.name
    }

    // Check for pattern matches (e.g., /customers/123 matches /customers)
    const segments = pathname.split('/').filter(Boolean)
    if (segments[0] === 'portfolio-tracker') {
      if (segments.length === 2 && segments[1] === 'results') return t('portfolioTracker:breadcrumb.results')
      if (segments.length === 3 && segments[2] === 'differences') return t('portfolioTracker:breadcrumb.differences')
      if (segments.length === 4 && segments[2] === 'strategies') {
        return trackerStrategyName ?? decodeURIComponent(segments[3])
      }
      return null
    }
    if (segments.length >= 1) {
      const basePath = '/' + segments[0]
      const baseRoute = routeNames.find((route) => route.path === basePath)
      if (baseRoute) {
        // Return specific labels for sub-routes
        if (segments.length === 2) {
          if (segments[1] === 'create') {
            return t('common:buttons.create', 'Create')
          }
          if (segments[0] === 'incoming-signals' && segments[1] === 'webhook-urls') {
            return t('incomingSignals:webhookUrls.header', 'Webhook URLs')
          }
          if (segments[0] === 'strategy-data-alerts' && segments[1] === 'webhook-urls') {
            return t('strategyDataAlerts:webhookUrls.header', 'Webhook URLs')
          }
          if (segments[0] === 'info-alerts' && segments[1] === 'webhook-urls') {
            return t('infoAlerts:webhookUrls.header', 'Webhook URLs')
          }
          if (segments[0] === 'market-candles' && segments[1] === 'fetch') {
            return t('marketCandles:trigger.button', 'Fetch Candles Now')
          }
          if (segments[0] === 'portfolio-builder' && segments[1] === 'new') {
            return t('portfolioBuilder:new.header', 'New revision')
          }
          if (segments[0] === 'incoming-signals' && segments[1] === 'send') {
            return t('incomingSignals:send.button', 'Send a signal')
          }
          if (baseRoute.hasDetails === false) {
            return null
          }
          return t('common:breadcrumb.details', 'Details')
        }
        if (segments.length === 3 && segments[2] === 'edit') {
          return t('common:buttons.edit', 'Edit')
        }
        if (segments.length === 3 && segments[2] === 'cleanse') {
          return t('strategyRequests:cleanse.steps.cleanse', 'Cleanse')
        }
        if (segments.length === 3 && segments[2] === 'replay') {
          return t('incomingSignals:replay.breadcrumb', 'Replay')
        }
        if (segments.length === 3 && segments[2] === 'change-status') {
          return t('common:breadcrumb.changeStatus', 'Change status')
        }
        if (segments.length === 3 && segments[2] === 'enable-real-time-updates') {
          return t('tradingStrategies:enableRealTimeUpdates.breadcrumb', 'Enable Real-Time Updates')
        }
        if (segments.length === 3 && segments[2] === 'turn-off-real-time-updates') {
          return t('tradingStrategies:turnOffRealTimeUpdates.breadcrumb', 'Turn Off Real-Time Updates')
        }
        if (segments.length === 3 && segments[2] === 'grant') {
          return t('offers:buttons.grant', 'Grant')
        }
        if (segments.length === 4 && segments[2] === 'tiers') {
          return segments[3] === 'create'
            ? t('common:buttons.create', 'Create')
            : t('membershipTiers:breadcrumb.tier', 'Tier')
        }
        if (segments.length === 5 && segments[2] === 'tiers') {
          switch (segments[4]) {
            case 'edit':
              return t('common:buttons.edit', 'Edit')
            case 'change-status':
              return t('common:breadcrumb.changeStatus', 'Change status')
          }
        }
      }
    }

    return null
  }

  const getBreadcrumbs = (location: string) => {
    const breadcrumbs: Array<{ pathname: string; name: string; active: boolean }> =
      []

    // Handle root path
    if (location === '/') {
      return breadcrumbs
    }

    const segments = location.split('/').filter(Boolean)
    let currentPath = ''

    segments.forEach((segment, index) => {
      currentPath = currentPath + '/' + segment
      const routeName = getRouteName(currentPath)

      if (routeName) {
        breadcrumbs.push({
          // The tracker's pages carry their setup in the query, so its crumbs keep it.
          pathname: segments[0] === 'portfolio-tracker' ? currentPath + search : currentPath,
          name: routeName,
          active: index === segments.length - 1,
        })
      }
    })

    return breadcrumbs
  }

  const breadcrumbs = getBreadcrumbs(currentLocation)

  return (
    <CBreadcrumb className="my-0">
      <CBreadcrumbItem>
        <Link to={routePaths.Dashboard}>{t('common:navigation.home')}</Link>
      </CBreadcrumbItem>
      {breadcrumbs.map((breadcrumb, index) =>
        breadcrumb.active ? (
          <CBreadcrumbItem key={index} active>
            {breadcrumb.name}
          </CBreadcrumbItem>
        ) : (
          <CBreadcrumbItem key={index}>
            <Link to={breadcrumb.pathname}>{breadcrumb.name}</Link>
          </CBreadcrumbItem>
        )
      )}
    </CBreadcrumb>
  )
}

export default React.memo(AppBreadcrumb)
